import React, { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { API_URL } from '../config';
import { usePermissions } from '../context/PermissionsContext';

interface LayoutProps {
  children: React.ReactNode;
  toggleTheme: () => void;
  currentTheme: string;
}

interface User {
  username: string;
  first_name: string;
  last_name: string;
  email: string;
}

const Layout: React.FC<LayoutProps> = ({ children, toggleTheme, currentTheme }) => {
  const { user, loading, hasPermission, logout } = usePermissions();
  const [openSections, setOpenSections] = useState<string[]>([]);
  const location = useLocation();
  const navigate = useNavigate();

  // Auto-open section based on current route
  useEffect(() => {
    const path = location.pathname;
    let sectionToOpen = '';

    if (['/reunioes', '/eventos', '/projetos', '/pesquisas', '/estudos-biblicos', '/ocorrencias', '/conteudos'].some(p => path.startsWith(p))) {
      sectionToOpen = 'operacional';
    } else if (['/pais', '/turmas', '/colaboradores', '/convidados'].some(p => path.startsWith(p))) {
      sectionToOpen = 'cadastros';
    } else if (['/relatorios', '/usuarios', '/perfis', '/configuracoes'].some(p => path.startsWith(p))) {
      sectionToOpen = 'administracao';
    }

    if (sectionToOpen) {
      setOpenSections(prev => {
        if (!prev.includes(sectionToOpen)) {
          return [...prev, sectionToOpen];
        }
        return prev;
      });
    }
  }, [location.pathname]);

  const handleLogout = () => {
    logout();
  };

  const toggleSection = (section: string) => {
    setOpenSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const isSectionOpen = (section: string) => openSections.includes(section);

  if (loading) return <div style={{ padding: '20px' }}>Carregando...</div>;

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="sidebar-logo">
          Conecta<span>Terra</span>
        </div>

        {user && (
          <div style={{ padding: '15px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: '10px' }}>
            <div style={{ fontSize: '0.9rem', fontWeight: 'bold' }}>
              {user.first_name ? `${user.first_name} ${user.last_name}` : user.username}
            </div>
            <div style={{ fontSize: '0.75rem', opacity: 0.7, marginBottom: '8px' }}>
              {user.email}
            </div>
            <div style={{ display: 'flex', gap: '5px' }}>
              <button
                onClick={() => navigate('/meu-perfil')}
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: 'white',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  flex: 1
                }}
              >
                Meu Perfil
              </button>
              <button
                onClick={handleLogout}
                style={{
                  background: 'rgba(255,50,50,0.2)',
                  border: '1px solid rgba(255,50,50,0.3)',
                  color: '#ffcccc',
                  padding: '4px 10px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '0.75rem',
                  flex: 1
                }}
              >
                Sair
              </button>
            </div>
          </div>
        )}

        <nav>
          <NavLink to="/" className="nav-item">Dashboard</NavLink>

          {/* Seção Operacional */}
          <div>
            <div
              className="nav-section"
              onClick={() => toggleSection('operacional')}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
            >
              OPERACIONAL
              <span style={{ fontSize: '0.8rem' }}>{isSectionOpen('operacional') ? '▲' : '▼'}</span>
            </div>
            {isSectionOpen('operacional') && (
              <div style={{ paddingLeft: '10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {hasPermission('view_meeting') && <NavLink to="/reunioes" className="nav-item">Reuniões</NavLink>}
                {hasPermission('view_event') && <NavLink to="/eventos" className="nav-item">Eventos</NavLink>}
                {hasPermission('view_project') && <NavLink to="/projetos" className="nav-item">Projetos</NavLink>}
                {hasPermission('view_survey') && <NavLink to="/pesquisas" className="nav-item">Pesquisas</NavLink>}
                {hasPermission('view_biblestudy') && <NavLink to="/estudos-biblicos" className="nav-item">Estudos Bíblicos</NavLink>}
                {hasPermission('view_incident') && <NavLink to="/ocorrencias" className="nav-item">Ocorrências</NavLink>}
                {hasPermission('view_content') && <NavLink to="/conteudos" className="nav-item">Conteúdos</NavLink>}
              </div>
            )}
          </div>

          {/* Seção Cadastros */}
          <div>
            <div
              className="nav-section"
              onClick={() => toggleSection('cadastros')}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
            >
              CADASTROS
              <span style={{ fontSize: '0.8rem' }}>{isSectionOpen('cadastros') ? '▲' : '▼'}</span>
            </div>
            {isSectionOpen('cadastros') && (
              <div style={{ paddingLeft: '10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {hasPermission('view_parent') && <NavLink to="/pais" className="nav-item">Pais / Responsáveis</NavLink>}
                {hasPermission('view_schoolclass') && <NavLink to="/turmas" className="nav-item">Turmas</NavLink>}
                {hasPermission('view_staff') && <NavLink to="/colaboradores" className="nav-item">Colaboradores</NavLink>}
                <NavLink to="/convidados" className="nav-item">Lista de Convidados</NavLink>
              </div>
            )}
          </div>

          {/* Seção Administração */}
          <div>
            <div
              className="nav-section"
              onClick={() => toggleSection('administracao')}
              style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}
            >
              ADMINISTRAÇÃO
              <span style={{ fontSize: '0.8rem' }}>{isSectionOpen('administracao') ? '▲' : '▼'}</span>
            </div>
            {isSectionOpen('administracao') && (
              <div style={{ paddingLeft: '10px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <NavLink to="/relatorios" className="nav-item">Relatórios</NavLink>
                {hasPermission('view_user') && <NavLink to="/usuarios" className="nav-item">Usuários</NavLink>}
                {hasPermission('view_group') && <NavLink to="/perfis" className="nav-item">Perfis de Acesso</NavLink>}
                {hasPermission('view_kpitarget') && <NavLink to="/configuracoes" className="nav-item">Configurações</NavLink>}
              </div>
            )}
          </div>
        </nav>

        <div style={{ padding: '20px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <button
            onClick={toggleTheme}
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: 'none',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              width: '100%',
              justifyContent: 'center'
            }}
          >
            {currentTheme === 'light' ? '🌙 Modo Escuro' : '☀️ Modo Claro'}
          </button>
        </div>
      </aside>
      <main className="main-content">
        {children}
      </main>
    </div>
  );
};

export default Layout;
